import { startTransition, useEffect, useState } from "react";
import { DEFAULT_SHIPPING_CONFIG, type ShippingConfig } from "@glastotaal/core";
import { getLeadTimeText, getShippingConfig } from "@/lib/catalog";
import { DEFAULT_ISO_TOESLAGEN, type IsoToeslagen } from "@/features/isolatieglas/isolatieglas-engine";
import { coerceBewerkingsTarieven, DEFAULT_BEWERKINGSTARIEVEN, type BewerkingsTarieven } from "@glastotaal/core";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";
import { firebaseIdle } from "@/lib/firebase/app";

/**
 * Runtime order-settings: read from Firestore `settings/global` (admin-editable,
 * public read) with the bundled seed as fallback. One cached doc read per session
 * serves both the shipping config and the levertijd-indicatie. Any field missing
 * in Firestore falls back to the seed/default, so older docs keep working.
 */
/** Inkoop-verzendkosten (Timmers) — alleen referentie/marge in de admin, niet in de prijs. */
export interface SupplierCosts {
  glassTransportCents: number;
  parcelNlCents: number;
  parcelBeCents: number;
  /** Speciaaltransport vacuümglas (BENGglas), Nederland — excl. btw. */
  benglasNlCents: number;
  /** Speciaaltransport vacuümglas (BENGglas), België — excl. btw. */
  benglasBeCents: number;
}

/** Standaard levertijd losse artikelen (uit voorraad, Timmers). */
export const DEFAULT_ARTICLES_LEAD_TIME = "2-3 werkdagen";

/**
 * Startwaarden voor de fijnmazige levertijden — functie, niet const, omdat de glas-groepen de
 * bestaande algemene levertijd overnemen (zelfde bewoording, zodat de pagina niet twee eenheden
 * naast elkaar toont zolang de beheerder ze nog niet verfijnd heeft).
 */
export function defaultLeadTimes(): LeadTimes {
  const glas = getLeadTimeText();
  return {
    glasOpMaat: glas,
    isolatieglas: glas,
    // Vacuümglas duurt structureel langer dan de rest; zelfde eenheid als de algemene tekst,
    // zodat een bezoeker de twee regels naast elkaar kan vergelijken.
    vacuumglas: "20–30 werkdagen",
    artikelOpVoorraad: DEFAULT_ARTICLES_LEAD_TIME,
    artikelOpBestelling: "5–10 werkdagen",
    artikelEigenTransport: "5–10 werkdagen",
  };
}

/**
 * Startwaarden voor de inkoop-verzendkosten: bewust 0 en NIET de echte Timmers-tarieven.
 *
 * Dit bestand belandt in de publieke JS-bundel, dus een bedrag dat hier hard staat is voor
 * iedereen te lezen — daarmee lag onze inkoopprijs op straat. De echte bedragen staan alleen in
 * settingsPrivate/global (admin-only) en worden in het instellingenscherm ingevuld; 0 betekent
 * hier simpelweg "nog niet ingevuld", niet "gratis".
 */
export const DEFAULT_SUPPLIER_COSTS: SupplierCosts = {
  glassTransportCents: 0,
  parcelNlCents: 0,
  parcelBeCents: 0,
  benglasNlCents: 0,
  benglasBeCents: 0,
};

/** Isolatieglas-varianten met een eigen marge: 4 dubbel-uitvoeringen + triple. */
export type IsoMarginKey = "standaard" | "basis" | "premium" | "sun" | "triple";
export const ISO_MARGIN_KEYS: IsoMarginKey[] = ["standaard", "basis", "premium", "sun", "triple"];
/** Vacuümglas (BENGglas)-lijnen met een eigen marge (= de 7 productlijnen). */
export type BengMarginKey = "single-coated" | "double-coated" | "triple-coated" | "monu" | "monu-restover" | "solar-control" | "sun";
export const BENG_MARGIN_KEYS: BengMarginKey[] = ["single-coated", "double-coated", "triple-coated", "monu", "monu-restover", "solar-control", "sun"];

/**
 * Levertijd-indicatie per productgroep. De twee losse velden hierboven (leadTimeText /
 * articlesLeadTimeText) waren te grof: al het maatwerkglas deelde één tekst, terwijl BENGglas
 * een heel andere doorlooptijd heeft dan gewoon glas op maat, en een artikel op voorraad iets
 * heel anders is dan een besteltartikel.
 *
 * LET OP — voorraad ≠ levertijd. De Timmers-API levert alléén `inStock`/`stock`; een levertijd
 * zit er niet in. De voorraadstand kiest dus wélke tekst je toont (artikelOpVoorraad vs
 * artikelOpBestelling), maar de tekst zelf komt altijd uit deze instellingen.
 *
 * Leeg veld = terugval op leadTimeText resp. articlesLeadTimeText, zodat er nooit een gat valt.
 */
export interface LeadTimes {
  /** Enkel glas, veiligheidsglas, spiegels — het gewone maatwerkglas. */
  glasOpMaat: string;
  /** Isolatieglas (dubbel/triple) — eigen productietijd. */
  isolatieglas: string;
  /** Vacuümglas (BENGglas) — langste doorlooptijd. */
  vacuumglas: string;
  /** Artikel dat bij Timmers op voorraad staat (API `inStock: true`). */
  artikelOpVoorraad: string;
  /** Artikel dat besteld moet worden (API `inStock: false`). */
  artikelOpBestelling: string;
  /**
   * Groot artikel dat met ons eigen transport gaat (verstekstans, vouwwand; aangevinkt in
   * Productbeheer) — rijdt mee op de glasroute, dus niet de pakket-levertijd.
   */
  artikelEigenTransport: string;
}
export type LeadTimeKey = keyof LeadTimes;

/**
 * Periode waarin niet geleverd wordt (bouwvak, feestdagen). Zolang `note` leeg is, is er geen
 * onderbreking en tonen we niets. Stond nergens op de site, terwijl het wel voorkomt.
 */
export interface DeliveryPause {
  /** Vrije tekst, bv. "Van 21 juli t/m 8 augustus leveren we niet — bestellingen daarna." */
  note: string;
}

interface GlobalSettings {
  shipping: ShippingConfig;
  leadTimeText: string;
  /** Levertijd-indicatie voor orders met alléén losse artikelen (geen glas). */
  articlesLeadTimeText: string;
  /** Fijnmazige levertijden per groep; leeg veld valt terug op de twee velden hierboven. */
  leadTimes: LeadTimes;
  /** Actieve leveronderbreking (vakantie/feestdagen); lege note = geen onderbreking. */
  deliveryPause: DeliveryPause;
  /** Bestelstop: geen betalingen mogelijk (bv. zolang de bankrekening nog niet openstaat). */
  orderStop: OrderStop;
  /** BENGglas-marge (%) — legacy/globaal, dient nog als fallback voor {@link bengMargins}. */
  bengMarginPct: number;
  /** BENGglas-marge (%) per lijn (single/double/triple-coated, monu, restover, solar, sun). */
  bengMargins: Record<BengMarginKey, number>;
  /** Isolatieglas-marge (%) — legacy/globaal, dient nog als fallback voor {@link isoMargins}. */
  isoMarginPct: number;
  /** Isolatieglas-marge (%) per variant (dubbel standaard/basis/premium/sun + triple). */
  isoMargins: Record<IsoMarginKey, number>;
  /** Toeslagen die Timmers bovenop de m²-prijs rekent (energie, afstandhouder). Staan niet in de
   *  prijslijst en niet in de API, dus alleen hier bij te werken. */
  isoToeslagen: IsoToeslagen;
  /** Inkooptarieven voor bewerkingen en instelkosten (Timmers). Komen niet uit de sync. */
  bewerkingsTarieven: BewerkingsTarieven;
  costs: SupplierCosts;
  /** Doeladres voor team-notificaties (nieuwe order/retour); leeg = env-fallback op de server. */
  notifyEmail: string;
  /** Doeladres voor montage-doorverwijzingen naar partner ProGlass; leeg = PROGLASS_TO-fallback. */
  proglassEmail: string;
}

export interface OrderStop {
  /** True = bestellen/betalen geblokkeerd; offerte-aanvragen blijven mogelijk. */
  active: boolean;
  /** Eigen tekst voor bezoekers; leeg = standaardzin. */
  note: string;
}

let cached: GlobalSettings | null = null;

const SHIPPING_FIELDS: (keyof ShippingConfig)[] = [
  "glassShippingCents",
  "glassShippingBeCents",
  "articlesShippingCents",
  "articlesShippingBeCents",
  "handlingGlassCents",
  "smallOrderThresholdCents",
  "smallOrderSurchargeCents",
  "benglasShippingNlCents",
  "benglasShippingBeCents",
  "benglasFreeShippingThresholdCents",
  "freeShippingThresholdCents",
  "longArticlesShippingCents",
  "longArticlesShippingBeCents",
];

function seedSettings(): GlobalSettings {
  return {
    shipping: getShippingConfig(),
    leadTimeText: getLeadTimeText(),
    articlesLeadTimeText: DEFAULT_ARTICLES_LEAD_TIME,
    leadTimes: defaultLeadTimes(),
    deliveryPause: { note: "" },
    // Standaard AAN: liever een dichte kassa dan een betaling op een rekening die er nog niet is.
    // De server hanteert dezelfde standaard; uitzetten doe je bewust in de admin.
    orderStop: { active: true, note: "" },
    bengMarginPct: 0,
    bengMargins: { "single-coated": 0, "double-coated": 0, "triple-coated": 0, monu: 0, "monu-restover": 0, "solar-control": 0, sun: 0 },
    isoMarginPct: 0,
    isoMargins: { standaard: 0, basis: 0, premium: 0, sun: 0, triple: 0 },
    isoToeslagen: { ...DEFAULT_ISO_TOESLAGEN, spacerPerM1Cents: { ...DEFAULT_ISO_TOESLAGEN.spacerPerM1Cents } },
    bewerkingsTarieven: { ...DEFAULT_BEWERKINGSTARIEVEN },
    costs: { ...DEFAULT_SUPPLIER_COSTS },
    notifyEmail: "",
    proglassEmail: "",
  };
}

/**
 * Toeslagen lezen. Een ontbrekend of onzinnig tarief valt terug op de code-standaard: liever het
 * laatst bekende juiste tarief dan stilzwijgend 0, want 0 rekent de toeslag niet en dat merk je
 * pas op de factuur van Timmers.
 */
function coerceIsoToeslagen(raw: Record<string, unknown> | undefined): IsoToeslagen {
  const energie = Number(raw?.energieCentPerKg);
  const spacers = { ...DEFAULT_ISO_TOESLAGEN.spacerPerM1Cents };
  const rawSpacers = (raw?.spacerPerM1Cents ?? {}) as Record<string, unknown>;
  for (const id of Object.keys(spacers)) {
    const v = Number(rawSpacers[id]);
    if (Number.isFinite(v) && v >= 0 && v <= 100_000) spacers[id] = v;
  }
  return {
    energieCentPerKg:
      Number.isFinite(energie) && energie >= 0 && energie <= 10_000 ? energie : DEFAULT_ISO_TOESLAGEN.energieCentPerKg,
    spacerPerM1Cents: spacers,
  };
}

/** Per-variant iso-marges lezen; ontbrekende varianten vallen terug op de (legacy) globale marge. */
function coerceIsoMargins(raw: Record<string, unknown> | undefined, fallback: number): Record<IsoMarginKey, number> {
  const out = {} as Record<IsoMarginKey, number>;
  for (const k of ISO_MARGIN_KEYS) {
    const v = Number(raw?.[k]);
    out[k] = Number.isFinite(v) && v >= 0 && v <= 500 ? v : fallback;
  }
  return out;
}

/** Per-lijn BENG-marges lezen; ontbrekende lijnen vallen terug op de (legacy) globale marge. */
function coerceBengMargins(raw: Record<string, unknown> | undefined, fallback: number): Record<BengMarginKey, number> {
  const out = {} as Record<BengMarginKey, number>;
  for (const k of BENG_MARGIN_KEYS) {
    const v = Number(raw?.[k]);
    out[k] = Number.isFinite(v) && v >= 0 && v <= 500 ? v : fallback;
  }
  return out;
}

/**
 * Levertijden per groep lezen. Een leeg/ontbrekend veld valt terug op de bijbehorende grove
 * tekst (glas-groepen → leadTimeText, artikel-groepen → articlesLeadTimeText), zodat een
 * half ingevulde admin nooit een lege levertijd op de site zet.
 */
function coerceLeadTimes(
  raw: Record<string, unknown> | undefined,
  glasFallback: string,
  artikelFallback: string,
): LeadTimes {
  const pick = (k: LeadTimeKey, fallback: string): string => {
    const v = raw?.[k];
    return typeof v === "string" && v.trim() ? v.trim() : fallback;
  };
  return {
    glasOpMaat: pick("glasOpMaat", glasFallback),
    isolatieglas: pick("isolatieglas", glasFallback),
    vacuumglas: pick("vacuumglas", glasFallback),
    artikelOpVoorraad: pick("artikelOpVoorraad", artikelFallback),
    artikelOpBestelling: pick("artikelOpBestelling", artikelFallback),
    // Nog niet ingevuld → de bestel-levertijd: een machine is zelden de snelste levering.
    artikelEigenTransport: pick("artikelEigenTransport", pick("artikelOpBestelling", artikelFallback)),
  };
}

function coerceCosts(raw: Record<string, unknown> | undefined): SupplierCosts {
  const out = { ...DEFAULT_SUPPLIER_COSTS };
  if (!raw) return out;
  for (const f of Object.keys(out) as (keyof SupplierCosts)[]) {
    const v = Number(raw[f]);
    if (Number.isFinite(v) && v >= 0) out[f] = v;
  }
  return out;
}

function coerceShipping(raw: Record<string, unknown> | undefined): ShippingConfig {
  const seed = getShippingConfig();
  if (!raw) return seed;
  const out = { ...seed };
  for (const f of SHIPPING_FIELDS) {
    const v = Number(raw[f]);
    if (Number.isFinite(v) && v >= 0) out[f] = v;
  }
  return out;
}

// useIsoMargins() en useBengMargins() monteren useGlobalSettings() allebei apart (o.a. binnen
// GlassCategories op de homepage). Zonder deze in-flight-gate zag elke instantie `cached === null`
// en startte elk zíjn EIGEN `readDoc()`.
let pending: Promise<GlobalSettings | null> | null = null;

function readDoc(): Promise<GlobalSettings | null> {
  return Promise.all([loadFirestoreModule(), getDb()])
    .then(([{ doc, getDoc }, db]) => getDoc(doc(db, "settings", "global")))
    .then((s) => {
      if (!s.exists()) return null;
      const data = s.data() as {
        shipping?: Record<string, unknown>;
        leadTimeText?: unknown;
        articlesLeadTimeText?: unknown;
        leadTimes?: Record<string, unknown>;
        deliveryPause?: Record<string, unknown>;
        orderStop?: Record<string, unknown>;
        bengMarginPct?: unknown;
        bengMargins?: Record<string, unknown>;
        isoMarginPct?: unknown;
        isoMargins?: Record<string, unknown>;
        isoToeslagen?: Record<string, unknown>;
        bewerkingsTarieven?: Record<string, unknown>;
      };
      const margin = Number(data.bengMarginPct);
      const bengLegacy = Number.isFinite(margin) && margin >= 0 && margin <= 500 ? margin : 0;
      const isoMargin = Number(data.isoMarginPct);
      const isoLegacy = Number.isFinite(isoMargin) && isoMargin >= 0 && isoMargin <= 500 ? isoMargin : 0;
      const leadTimeText =
        typeof data.leadTimeText === "string" && data.leadTimeText.trim()
          ? data.leadTimeText
          : getLeadTimeText();
      const articlesLeadTimeText =
        typeof data.articlesLeadTimeText === "string" && data.articlesLeadTimeText.trim()
          ? data.articlesLeadTimeText
          : DEFAULT_ARTICLES_LEAD_TIME;
      const pauseNote = data.deliveryPause?.note;
      return {
        shipping: coerceShipping(data.shipping),
        leadTimeText,
        articlesLeadTimeText,
        leadTimes: coerceLeadTimes(data.leadTimes, leadTimeText, articlesLeadTimeText),
        deliveryPause: { note: typeof pauseNote === "string" ? pauseNote.trim() : "" },
        orderStop: {
          active: data.orderStop?.active !== false,
          note: typeof data.orderStop?.note === "string" ? (data.orderStop.note as string).trim() : "",
        },
        bengMarginPct: bengLegacy,
        bengMargins: coerceBengMargins(data.bengMargins, bengLegacy),
        isoMarginPct: isoLegacy,
        isoMargins: coerceIsoMargins(data.isoMargins, isoLegacy),
        isoToeslagen: coerceIsoToeslagen(data.isoToeslagen),
        bewerkingsTarieven: coerceBewerkingsTarieven(data.bewerkingsTarieven),
        // costs/notifyEmail/proglassEmail staan bewust NIET in dit (publieke) document meer —
        // zie fetchPrivateSettings voor de admin-only bron.
        costs: { ...DEFAULT_SUPPLIER_COSTS },
        notifyEmail: "",
        proglassEmail: "",
      };
    })
    .catch(() => null);
}

function loadOnce(): Promise<GlobalSettings | null> {
  if (cached != null) return Promise.resolve(cached);
  if (!pending) {
    pending = firebaseIdle().then(readDoc).then((c) => {
      pending = null;
      if (c != null) cached = c;
      return c;
    });
  }
  return pending;
}

/** React to a freshly read settings doc; seed value until Firestore resolves. */
function useGlobalSettings(): GlobalSettings {
  const [v, setV] = useState<GlobalSettings>(cached ?? seedSettings());
  useEffect(() => {
    if (cached != null) return;
    // Meerdere secties wachten hierop tegelijk — zonder startTransition zet React al hun
    // re-renders in ÉÉN synchrone, niet-onderbreekbare taak (zie catalog-overrides.ts).
    void loadOnce().then((c) => {
      if (c != null) startTransition(() => setV(c));
    });
  }, []);
  return v;
}

/** Live shipping config; seed value until the Firestore read resolves. */
export function useShippingConfig(): ShippingConfig {
  return useGlobalSettings().shipping;
}

/** Live levertijd-indicatie (glas op maat); seed value until the Firestore read resolves. */
export function useLeadTimeText(): string {
  return useGlobalSettings().leadTimeText;
}

/** Live levertijd-indicatie voor orders met alléén losse artikelen. */
export function useArticlesLeadTimeText(): string {
  return useGlobalSettings().articlesLeadTimeText;
}

/** Levertijd van één productgroep (fijnmaziger dan {@link useLeadTimeText}). */
export function useLeadTime(key: LeadTimeKey): string {
  return useGlobalSettings().leadTimes[key];
}

/** Alle groep-levertijden ineens (voor overzichten zoals /levertijden). */
export function useLeadTimes(): LeadTimes {
  return useGlobalSettings().leadTimes;
}

/**
 * Levertijd van een artikel op basis van de Timmers-voorraad. De API kent geen levertijd, alleen
 * voorraad — die kiest dus alleen wélke beheerde tekst je toont. `undefined` (voorraad onbekend)
 * valt bewust terug op de voorraad-tekst: dat is wat er vóór deze structuur ook stond.
 */
export function useArticleLeadTime(inStock: boolean | undefined): string {
  const lt = useGlobalSettings().leadTimes;
  return inStock === false ? lt.artikelOpBestelling : lt.artikelOpVoorraad;
}

/**
 * De levertijd-sleutel die bij één winkelwagenregel hoort.
 *
 * Losse functie (geen hook), zodat de winkelwagen, het afrekenscherm en de configurator
 * allemaal dezelfde afweging maken. Dat liep uiteen: alleen de productpagina las de fijnmazige
 * instellingen, de rest gebruikte twee grove teksten. Zolang alle velden dezelfde waarde hebben
 * valt dat niet op — zet je vacuümglas op zes weken, dan belooft de productpagina zes weken en
 * de winkelwagen nog altijd "2-3 weken".
 */
export function leadTimeKeyForKind(kind: string, inStock?: boolean, eigenTransport?: boolean): LeadTimeKey {
  // Een artikel dat met eigen transport gaat (isVrachtItem) volgt die levertijd, niet de pakketdienst.
  if (kind === "article" && eigenTransport) return "artikelEigenTransport";
  switch (kind) {
    case "benglas":
      return "vacuumglas";
    case "isolatieglas":
      return "isolatieglas";
    case "glass":
    case "shower":
      // Douche = maatwerkglas + beslag; het glas bepaalt de doorlooptijd.
      return "glasOpMaat";
    default:
      return inStock === false ? "artikelOpBestelling" : "artikelOpVoorraad";
  }
}

/** Volgorde van kort naar lang: bij een gemengde wagen telt de tráágste regel. */
const LEAD_TIME_ZWAARTE: LeadTimeKey[] = [
  "artikelOpVoorraad",
  "artikelOpBestelling",
  "artikelEigenTransport",
  "glasOpMaat",
  "isolatieglas",
  "vacuumglas",
];

/**
 * Levertijd voor een hele winkelwagen: de traagste regel wint, want de klant krijgt één zending.
 * Lege wagen → de gewone artikel-levertijd (er valt dan niets te beloven).
 */
export function useCartLeadTime(items: { kind: string; inStock?: boolean; eigenTransport?: boolean }[]): string {
  const lt = useGlobalSettings().leadTimes;
  if (items.length === 0) return lt.artikelOpVoorraad;
  let zwaarste: LeadTimeKey = "artikelOpVoorraad";
  for (const it of items) {
    const k = leadTimeKeyForKind(it.kind, it.inStock, it.eigenTransport);
    if (LEAD_TIME_ZWAARTE.indexOf(k) > LEAD_TIME_ZWAARTE.indexOf(zwaarste)) zwaarste = k;
  }
  return lt[zwaarste];
}

/** Actieve leveronderbreking (bouwvak/feestdagen); lege string = geen onderbreking. */
export function useDeliveryPause(): string {
  return useGlobalSettings().deliveryPause.note;
}

/** Live isolatieglas-marge (%) voor een specifieke variant; valt terug op de legacy globale marge. */
export function useIsoMargin(variant: IsoMarginKey): number {
  return useGlobalSettings().isoMargins[variant];
}
/** Alle isolatieglas-marges (%) per variant — voor plekken die meerdere varianten tegelijk tonen. */
export function useBewerkingsTarieven(): BewerkingsTarieven {
  return useGlobalSettings().bewerkingsTarieven;
}

export function useIsoToeslagen(): IsoToeslagen {
  return useGlobalSettings().isoToeslagen;
}

export function useIsoMargins(): Record<IsoMarginKey, number> {
  return useGlobalSettings().isoMargins;
}
/** Live BENGglas-marge (%) voor een specifieke lijn; valt terug op de legacy globale marge. */
export function useBengMargin(line: BengMarginKey): number {
  return useGlobalSettings().bengMargins[line];
}
/** Alle BENGglas-marges (%) per lijn — voor plekken die meerdere lijnen tegelijk tonen (landing). */
export function useBengMargins(): Record<BengMarginKey, number> {
  return useGlobalSettings().bengMargins;
}

/** One-off fetch for forms; returns the live values or the seed fallback. */
export async function fetchSettings(): Promise<GlobalSettings> {
  const c = await readDoc();
  if (c != null) cached = c;
  return c ?? seedSettings();
}

/** Interne instellingen (inkoopkosten + notificatie-adressen). */
export interface PrivateSettings {
  costs: SupplierCosts;
  notifyEmail: string;
  proglassEmail: string;
}

/**
 * Interne instellingen ophalen uit settingsPrivate/global. Vereist het recht 'settings'; voor
 * iedereen anders weigeren de Firestore-regels de leesactie en krijg je de lege startwaarden.
 * Bewust een aparte functie: deze gegevens horen niet in de publieke settings-stroom die elke
 * bezoeker binnenhaalt voor verzendkosten en levertijden.
 */
export async function fetchPrivateSettings(): Promise<PrivateSettings> {
  const leeg: PrivateSettings = { costs: { ...DEFAULT_SUPPLIER_COSTS }, notifyEmail: "", proglassEmail: "" };
  try {
    const [{ doc, getDoc }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const s = await getDoc(doc(db, "settingsPrivate", "global"));
    if (!s.exists()) return leeg;
    const d = s.data() as { costs?: Record<string, unknown>; notifyEmail?: unknown; proglassEmail?: unknown };
    let costs = coerceCosts(d.costs);
    // Terugval op het oude publieke settings/global zolang de inkooptarieven nog niet in het
    // privé-document zijn opgeslagen. Zonder deze regel leest de bestelbon nullen en verdwijnt de
    // transportregel geruisloos — dan lijkt de bestelling goedkoper dan wat we Timmers betalen.
    if (costs.glassTransportCents === 0 && costs.parcelNlCents === 0 && costs.parcelBeCents === 0) {
      const oud = await getDoc(doc(db, "settings", "global"));
      if (oud.exists()) costs = coerceCosts((oud.data() as { costs?: Record<string, unknown> }).costs);
    }
    return {
      costs,
      notifyEmail: typeof d.notifyEmail === "string" ? d.notifyEmail : "",
      proglassEmail: typeof d.proglassEmail === "string" ? d.proglassEmail : "",
    };
  } catch {
    return leeg;
  }
}

/** Update the session cache after an admin save so other views reflect it. */
export function setCachedSettings(next: Partial<GlobalSettings>): void {
  cached = { ...(cached ?? seedSettings()), ...next };
}

export { DEFAULT_SHIPPING_CONFIG };

/** Bestelstop (kassa dicht) — de server hanteert dezelfde regel, dit is alleen de weergave. */
export function useOrderStop(): OrderStop {
  return useGlobalSettings().orderStop;
}
